import { Flex, RectangularButton, Text } from '@resideo/blueprint-react';
import { NotificationTypeEnum } from '@resideo/web-integration-services-api-client';
import { useQuery } from '@tanstack/react-query';
import AppLayout from 'components/common/AppLayout';
import NoticeLayoutRedesign from 'components/common/NoticeLayoutRedesign';
import { PRO_PORTAL_URL } from 'config';
import { useRisClient } from 'hooks/useRisClient';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Link as ReactRouterLink,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { useAuth } from 'utils/auth';
import { Titled } from 'react-titled';
import styled from 'styled-components';

const Wrapper = styled.div`
  margin-bottom: 50px;
  margin-top: 10px;
`;

const FooterContainer = styled.div`
  margin: 20px;
  padding-bottom: 20px;
`;

const deserializeInviteToken = inviteToken => {
  const [inviteId = '', email = '', company = ''] = window
    .atob(inviteToken)
    .split('|');
  return {
    inviteId,
    email,
    company,
  };
};

const InviteDisplayPanel = () => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { client } = useRisClient();
  const navigate = useNavigate();
  const inviteId = searchParams.get('inviteId');

  const { data } = useQuery({
    queryKey: ['getInviteById', { inviteId }],
    queryFn: async () => {
      const { inviteId: token } = deserializeInviteToken(inviteId);
      const res = await client.public.publicGetInvitation(token || '');
      return res?.data;
    },
    enabled: !!inviteId,
  });

  const { loginWithRedirect } = useAuth();

  const invitedUserLink = event => {
    event.preventDefault();

    loginWithRedirect({
      isNewUser: true,
      email: data?.sendToEmail ?? '',
      appState: {
        returnTo: `${PRO_PORTAL_URL}/sign-up/accept-invite?invite=${data?.inviteToken}`,
      },
    });
  };

  return (
    <Titled title={title => `${t('getStarted.pageTitle')} - ${title}`}>
      <AppLayout
        showHeader={false}
        showLeftNav={false}
        showGreyPanel={true}
        showFooter={false}>
        <NoticeLayoutRedesign
          title={`${t('user.signUp.inviteForm.inviteTo')} ${data?.partnerAccount?.name || ''}`}
          showFooter={false}
          defaultPadding={false}>
          <Wrapper>
            <Text
              marginBottom='large'
              fontSize='18px'
              fontWeight={'500'}
              lineHeight={'20px'}
              data-test-invite-text2>
              {t('user.signUp.inviteForm.inviteText')}
            </Text>
            <Text
              marginBottom='large'
              fontSize='18px'
              fontWeight={'500'}
              lineHeight={'20px'}
              data-test-invite-note>
              {t('user.signUp.inviteForm.inviteNote', {
                companyName: data?.partnerAccount?.name || '',
              })}
            </Text>
            <RectangularButton
              variant='primary'
              onClick={() => {
                const phoneUrl =
                  data?.notificationType === NotificationTypeEnum.SMS &&
                  data?.sendToPhoneNumber
                    ? `&phoneNumber=${data?.sendToPhoneNumber}`
                    : null;
                navigate(
                  `/sign-up?invite=${data?.inviteToken}${phoneUrl ? phoneUrl : ''}`
                );
              }}
              data-test-invite-form-sign-up-btn>
              {t('user.signUp.inviteForm.signUpBtn')}
            </RectangularButton>
          </Wrapper>
        </NoticeLayoutRedesign>
        <FooterContainer className='common-input'>
          <Flex justifyContent='center' marginBottom={8}>
            <Text
              fontSize='18px'
              marginRight='small'
              data-test-invite-sign-in-text>
              {t('user.signUp.inviteForm.signInText')}
            </Text>
            <ReactRouterLink
              to='#'
              onClick={invitedUserLink}
              data-test-invite-sign-in-link>
              {t('signIn.button')}
            </ReactRouterLink>
          </Flex>
        </FooterContainer>
      </AppLayout>
    </Titled>
  );
};

export default InviteDisplayPanel;
